import React, { useEffect, useState, useCallback } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import {
  Loader2, Bike, MapPin, ShieldCheck, ChevronDown, ChevronRight, RefreshCw, AlertTriangle,
} from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";

// Phase 3A.5 — Read-only staff view of the Local Delivery + address-
// validation payload attached to a paid retail order. Rendered inside
// OrderDetailsDrawer when the order has fulfillment.mode === "delivery".
// Mirrors OrderShippingPanel's read-only contract, but deliberately a
// SEPARATE component/data path (Local Delivery stays isolated from
// Parcel Shipping in UI, per the engineering standards).
//
// Contract:
//   GET /api/retail/local-delivery/orders/{order_id}
//   404 → order has no Local Delivery record yet (drawer omits the panel).
//   200 → { local_delivery, quotes, dispatch_attempts, events, address_validation }
//
// Tenant-owned production dispatch (Aug 2026) — this panel is no
// longer purely read-only: when the order's own `payment_status` is
// `awaiting_tenant_acceptance` (passed down as the `paymentStatus`
// prop from OrderDetailsDrawer), it renders the ONE tenant-facing
// action — "Accept Order & Request Uber" — that may create a real
// Uber Direct delivery. Every other status keeps this panel exactly as
// read-only as before. The backend, never this component, is the
// authoritative eligibility gate: a click when the order is no longer
// eligible (already accepted by a concurrent click, a duplicate tab,
// etc.) returns a safe "already_processed" outcome, never a second
// dispatch.

function fmtMoney(v, currency) {
  if (v == null) return "—";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return sym ? `${sym}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${currency || ""}`.trim();
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function mapUrlFor(lat, lng) {
  if (lat == null || lng == null) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
}

const VALIDATION_STATUS_LABEL = {
  verified: "Verified",
  needs_confirmation: "Needs confirmation",
  invalid_or_imprecise: "Invalid / imprecise",
};

const VALIDATION_STATUS_COLOR = {
  verified: "text-emerald-700 bg-emerald-50 border-emerald-200",
  needs_confirmation: "text-sky-700 bg-sky-50 border-sky-200",
  invalid_or_imprecise: "text-rose-700 bg-rose-50 border-rose-200",
};

// Business-friendly labels for the raw internal `DeliveryStatus`
// vocabulary — a tenant operator should never have to know what
// "dispatch_pending" or "en_route_to_dropoff" means as a raw string.
// See `backend/retail/delivery/dispatch/domain/models.py::
// DeliveryStatus` for the authoritative source of these values.
const DELIVERY_STATUS_LABEL = {
  not_applicable: "Not applicable",
  quote_pending: "Getting delivery quote…",
  quoted: "Quote received",
  quote_expired: "Quote expired",
  dispatch_pending: "Requesting courier…",
  courier_requested: "Courier requested",
  courier_assigned: "Courier assigned",
  en_route_to_pickup: "Courier en route to pickup",
  at_pickup: "Courier at restaurant",
  picked_up: "Picked up by courier",
  en_route_to_dropoff: "Out for delivery",
  delivered: "Delivered",
  cancellation_pending: "Cancellation pending",
  canceled: "Delivery canceled",
  delivery_failed: "Delivery request failed",
  returned: "Returned",
  reconciliation_required: "Needs attention",
};

const DELIVERY_STATUS_COLOR = {
  delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
  courier_requested: "text-sky-700 bg-sky-50 border-sky-200",
  courier_assigned: "text-sky-700 bg-sky-50 border-sky-200",
  en_route_to_pickup: "text-sky-700 bg-sky-50 border-sky-200",
  at_pickup: "text-sky-700 bg-sky-50 border-sky-200",
  picked_up: "text-sky-700 bg-sky-50 border-sky-200",
  en_route_to_dropoff: "text-sky-700 bg-sky-50 border-sky-200",
  delivery_failed: "text-rose-700 bg-rose-50 border-rose-200",
  canceled: "text-rose-700 bg-rose-50 border-rose-200",
  reconciliation_required: "text-amber-700 bg-amber-50 border-amber-200",
  quote_expired: "text-amber-700 bg-amber-50 border-amber-200",
};

// Own Delivery/KDS status-wording correction (Aug 2026) — this panel
// is no longer mounted for an Own Delivery order in the normal drawer
// flow (see `OrderDetailsDrawer.jsx`'s render gate), but the backend
// endpoint this panel calls (`GET /local-delivery/orders/{order_id}`)
// is NOT itself provider-gated and returns real Own Delivery data if
// ever reached another way. Defense-in-depth: `DELIVERY_STATUS_LABEL`
// above is Uber's own courier vocabulary and must never be shown for
// `provider === "own_delivery"` (e.g. "dispatch_pending" ->
// "Requesting courier…" is actively false for a provider that never
// requests an Uber courier). This mirrors `OwnDeliveryOperationalPanel.
// jsx`'s own `PHASE_LABEL` (kept as a separate copy, matching this
// file's own existing convention for the Uber-facing labels above).
const OWN_DELIVERY_PHASE_LABEL = {
  accepted: "Accepted",
  preparing: "Preparing",
  ready_for_delivery: "Ready for Delivery",
  driver_assigned: "Driver Assigned",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  delivery_failed: "Delivery Failed",
  delivery_cancelled: "Delivery Cancelled",
};

const OWN_DELIVERY_PHASE_COLOR = {
  delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
  driver_assigned: "text-indigo-700 bg-indigo-50 border-indigo-200",
  out_for_delivery: "text-indigo-700 bg-indigo-50 border-indigo-200",
  ready_for_delivery: "text-sky-700 bg-sky-50 border-sky-200",
  preparing: "text-sky-700 bg-sky-50 border-sky-200",
  delivery_failed: "text-rose-700 bg-rose-50 border-rose-200",
  delivery_cancelled: "text-rose-700 bg-rose-50 border-rose-200",
};

// Gate C5A §6/§9 — every label names the ACTOR.
//
// This map used to read `accepted: "Accepted"`, next to a field labelled
// "Quote status". Three unrelated facts have worn that word here — the
// customer accepting a fee, the tenant accepting the order, and Uber
// accepting a delivery — and the one it actually meant (the first) is
// the one a staff member is least likely to assume. Reading "Accepted"
// beside an expired quote, the reasonable conclusion is that the
// delivery is handled; in fact no courier was ever booked and a
// customer's card is sitting on an authorization.
//
// `rejected` is rendered as the customer's decision having lapsed
// because that is the only way this codebase reaches it for a quote:
// `quotes_repo.mark_customer_decision_expired` (the 3:00 window) is its
// sole writer.
const QUOTE_STATUS_LABEL = {
  pending: "Quote received — awaiting customer",
  accepted: "Customer accepted quote",
  rejected: "Customer decision expired",
  expired: "Provider quote expired",
  superseded: "Quote superseded",
};

// When a quote the customer DID accept later expires or is superseded,
// the stored status alone would erase that acceptance from the screen.
// `customer_accepted_at` is never cleared precisely so this compound
// label can keep the history visible.
function quoteStatusLabel(quote) {
  if (!quote || !quote.status) return "—";
  const base = QUOTE_STATUS_LABEL[quote.status] || quote.status;
  if (quote.customer_accepted_at && (quote.status === "expired" || quote.status === "superseded")) {
    return `Customer accepted quote — ${base.toLowerCase()}`;
  }
  // Gate C5A-5E — this row's `customer_accepted_at` may have been
  // CARRIED FORWARD from an earlier attempt rather than written by a
  // customer acting on this quote. Both are honest facts and they are
  // not the same fact, so the screen distinguishes them: the customer
  // approved this exact price for this exact address, but not on this
  // row. Flattening the two into a bare "Customer accepted quote" is
  // the same class of ambiguity the compound label above exists to
  // remove.
  if (quote.status === "accepted" && quote.approval_inherited_from_quote_id) {
    return "Customer-approved fee — carried forward to the new quote";
  }
  return base;
}

function fallbackAddressLines(snapshot) {
  if (!snapshot) return null;
  const cityLine = [snapshot.city, snapshot.state, snapshot.postal_code].filter(Boolean).join(", ");
  return (
    <>
      {snapshot.line1 || "—"}
      {snapshot.line2 ? `\n${snapshot.line2}` : ""}
      {cityLine ? `\n${cityLine}` : ""}
    </>
  );
}

export default function OrderLocalDeliveryPanel({ orderId, paymentStatus, onAccepted }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [techOpen, setTechOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ loading: !s.data, error: null, data: s.data }));
    api.get(`/retail/local-delivery/orders/${orderId}`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => {
        if (e?.response?.status === 404) {
          setState({ loading: false, error: "not_a_local_delivery_order", data: null });
        } else {
          setState({ loading: false, error: "load_failed", data: null });
        }
      });
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const acceptAndDispatch = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/retail/local-delivery/orders/${orderId}/accept-and-dispatch`);
      setAccepted(true);
      setConfirmOpen(false);
      if (data?.status === "dispatched") {
        toast.success(data.message || "Courier requested.");
      } else if (data?.status === "already_processed") {
        toast.message(data.message || "This order has already been accepted.");
      } else {
        // quote_expired_payment_requires_refund / dispatch_failed_payment_requires_refund
        // / reconciliation_required — all business-friendly text already,
        // never a raw internal status string. Shown as a warning, not a
        // success toast, since none of these mean a courier was requested.
        toast.warning(data?.message || "This order could not be dispatched. Please check its status.");
      }
      load();
      onAccepted?.();
    } catch (e) {
      toast.error(e);
      // A failed request may still have left the order eligible (network
      // error before the server processed it) or already claimed by this
      // same click (a slow response after the CAS succeeded) — reload
      // rather than assume either way, so the button's own eligibility
      // check (derived from fresh `paymentStatus`/`ld.status` below)
      // decides what's shown next, never a stale local guess.
      load();
      onAccepted?.();
    } finally {
      setAccepting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="py-4 grid place-items-center text-slate-400" data-testid="local-delivery-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (state.error === "not_a_local_delivery_order") return null;
  if (state.error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="local-delivery-panel-error">
        Could not load Local Delivery details.
      </div>
    );
  }
  if (!state.data) return null;

  const d = state.data;
  const ld = d.local_delivery || {};
  const av = d.address_validation || null;
  const quotes = d.quotes || [];
  // Gate C5A-5E — show the quote the ORDER names, not merely the newest
  // row. Those are the same thing in the ordinary case and were NOT the
  // same after a replacement quote was minted: the newest row was the
  // replacement while every operational surface still used the previous
  // one, so this panel read "Quote received — awaiting customer" beside
  // a staff card reading "Customer accepted quote — provider quote
  // expired". Falls back to newest only when the server did not name a
  // canonical quote at all (an order that never had one).
  const canonicalQuoteId = d.canonical_quote_id || null;
  const latestQuote =
    (canonicalQuoteId && quotes.find((q) => q.id === canonicalQuoteId)) || quotes[0] || {};
  const currency = ld.currency || latestQuote.currency || "USD";
  const mapUrl = av ? mapUrlFor(av.latitude, av.longitude) : null;
  const dropoff = ld.dropoff_snapshot || {};
  // Go-live correction (Aug 2026), Scope E — `provider_delivery_id`
  // lives on a dispatch ATTEMPT, never on `local_delivery` itself.
  const attempts = d.dispatch_attempts || [];
  const currentAttempt = attempts.find((a) => a.id === ld.current_dispatch_attempt_id) || attempts[attempts.length - 1] || {};
  const providerDeliveryIdSuffix = currentAttempt.provider_delivery_id_suffix || null;

  // Backend is the authoritative eligibility gate (`tenant_acceptance_
  // service.accept_and_dispatch`'s own CAS) — this is a UI convenience
  // only, so the button doesn't even render for an order that's
  // obviously not eligible, but a stale render never causes a second
  // dispatch: the backend CAS would just return `already_processed`.
  const canAccept = paymentStatus === "awaiting_tenant_acceptance" && !accepted && !accepting;
  const needsAttention = ld.status === "reconciliation_required";
  const dispatchFailed = ld.status === "delivery_failed";
  // Terminal-status synchronization correction (Aug 2026) — `delivery_
  // failed` covers two materially different outcomes that must not
  // share one message: (1) Uber rejected Create Delivery outright, in
  // which case no delivery was ever created and the payment was
  // AUTOMATICALLY refunded by the existing pre-dispatch safety net
  // (`payments/engine.py::_refund_unfulfillable_payment`); (2) a real,
  // already-dispatched delivery (a `provider_delivery_id` exists —
  // courier was requested, tracking worked) later failed in-flight
  // (e.g. no courier ever became available) — this is deliberately
  // NEVER auto-refunded (see `event_application.py`'s own post-
  // dispatch-failure alert), so telling the operator "refund already
  // handled" here would be actively false. `providerDeliveryIdSuffix`
  // (computed above) is the one signal already available on this
  // panel that distinguishes the two.
  const wasDispatched = Boolean(providerDeliveryIdSuffix);
  const events = d.events || [];
  const latestFailureReason = dispatchFailed
    ? (events.find((e) => e.normalized_status === "delivery_failed")?.meta || {}).undeliverable_reason
      || events.find((e) => e.normalized_status === "delivery_failed")?.provider_native_status
    : null;

  return (
    <section data-testid="order-local-delivery-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <Bike className="w-4 h-4 text-orange-600" />
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Local Delivery
        </div>
        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">
          LOCAL
        </span>
        <button
          type="button"
          onClick={load}
          className="ml-auto text-slate-400 hover:text-slate-700"
          title="Refresh delivery status"
          data-testid="local-delivery-panel-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Tenant-owned production dispatch — the one primary action. */}
      {canAccept && (
        <div
          className="rounded-md border border-indigo-200 bg-indigo-50 p-3 flex items-center justify-between gap-3"
          data-testid="local-delivery-panel-accept-card"
        >
          <div className="text-sm text-indigo-900">
            This paid order is awaiting your acceptance before a courier is requested.
          </div>
          <Button onClick={() => setConfirmOpen(true)} data-testid="local-delivery-accept-btn">
            Accept Order &amp; Request Uber
          </Button>
        </div>
      )}

      {/* Tenant-visible failure/attention banner — the alert this used
          to be internal-ops-only is now shown directly here. */}
      {(needsAttention || dispatchFailed) && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-amber-900 text-xs"
          data-testid="local-delivery-panel-attention-banner"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            {needsAttention ? (
              "We couldn't confirm whether the courier request went through. Our team has been alerted — please do not retry, and contact support if this doesn't resolve shortly."
            ) : wasDispatched ? (
              <>
                A courier was requested for this order, but the delivery could not be
                completed{latestFailureReason ? ` (${String(latestFailureReason).replace(/_/g, " ")})` : ""}.
                {" "}The payment has <strong>not</strong> been automatically refunded — please
                contact the customer to arrange pickup, a replacement delivery, or a manual refund.
              </>
            ) : (
              "Uber Direct could not create this delivery. If the customer's payment required a refund, that has already been handled automatically."
            )}
          </div>
        </div>
      )}

      {/* Address integrity */}
      {av && (
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid="local-delivery-panel-address">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-3.5 h-3.5 text-slate-500" />
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Delivery address</div>
            <span
              className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${VALIDATION_STATUS_COLOR[av.status] || "text-slate-600 bg-slate-100 border-slate-200"}`}
              data-testid="local-delivery-panel-validation-status"
            >
              {VALIDATION_STATUS_LABEL[av.status] || av.status}
            </span>
          </div>
          <div className="text-sm text-slate-900 whitespace-pre-line">
            {av.normalized_address?.line1 || "—"}
            {av.normalized_address?.line2 ? `\n${av.normalized_address.line2}` : ""}
            {(av.normalized_address?.city || av.normalized_address?.state || av.normalized_address?.postal_code)
              ? `\n${[av.normalized_address?.city, av.normalized_address?.state, av.normalized_address?.postal_code].filter(Boolean).join(", ")}`
              : ""}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="inline-flex items-center gap-1 text-slate-600">
              <ShieldCheck className="w-3 h-3" />
              {av.customer_confirmed_at ? `Confirmed ${fmtDate(av.customer_confirmed_at)}` : "Not customer-confirmed"}
            </span>
            {mapUrl && (
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="underline text-slate-600"
                data-testid="local-delivery-panel-map-link"
              >
                Open in map
              </a>
            )}
          </div>
        </div>
      )}
      {/* Fallback — this order predates address validation, or the
          tenant hasn't enabled it. `local_delivery.dropoff_snapshot` is
          the SAME address this order's own quote/dispatch calls
          already use — never a second, independently-derived source —
          so the tenant is never left looking at a blank address. */}
      {!av && (
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid="local-delivery-panel-address-fallback">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-3.5 h-3.5 text-slate-500" />
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Delivery address</div>
          </div>
          {dropoff.line1 ? (
            <div className="text-sm text-slate-900 whitespace-pre-line">{fallbackAddressLines(dropoff)}</div>
          ) : (
            <div className="text-xs text-slate-500">Address not yet on file for this order.</div>
          )}
        </div>
      )}

      {/* Delivery instructions — captured at checkout, shown here so
          the tenant (who currently has no automated way to relay this
          to the courier) can act on it manually if needed. */}
      {dropoff.instructions && (
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid="local-delivery-panel-instructions">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Delivery instructions</div>
          <div className="text-sm text-slate-900 whitespace-pre-line">{dropoff.instructions}</div>
        </div>
      )}

      {/* Provider status */}
      <div className="grid grid-cols-2 gap-3" data-testid="local-delivery-panel-status">
        <Field
          label="Delivery status"
          value={
            ld.provider === "own_delivery" ? (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${OWN_DELIVERY_PHASE_COLOR[ld.own_delivery_phase] || "text-slate-600 bg-slate-100 border-slate-200"}`}
              >
                {OWN_DELIVERY_PHASE_LABEL[ld.own_delivery_phase] || ld.own_delivery_phase || "Accepted"}
              </span>
            ) : (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${DELIVERY_STATUS_COLOR[ld.status] || "text-slate-600 bg-slate-100 border-slate-200"}`}
              >
                {DELIVERY_STATUS_LABEL[ld.status] || ld.status || "—"}
              </span>
            )
          }
        />
        <Field label="Provider" value={ld.provider || latestQuote.provider || "—"} />
        <Field
          label="Quote status"
          value={
            <span data-testid="local-delivery-panel-quote-status">
              {quoteStatusLabel(latestQuote)}
            </span>
          }
        />
        <Field label="Quote expires" value={fmtDate(latestQuote.expires_at)} />
        <Field label="Delivery fee" value={fmtMoney(latestQuote.customer_fee_amount, currency)} />
        <Field label="Est. pickup" value={fmtDate(latestQuote.estimated_pickup_at)} />
        <Field label="Est. drop-off" value={fmtDate(latestQuote.estimated_dropoff_at)} />
        <Field label="Tracking URL" value={
          ld.tracking_url
            ? <a href={ld.tracking_url} target="_blank" rel="noopener noreferrer nofollow" className="underline">Track delivery</a>
            : "—"
        } />
        {providerDeliveryIdSuffix && (
          <Field
            label="Provider delivery ID"
            value={<span data-testid="local-delivery-panel-provider-id-suffix">{`Uber ID: ${providerDeliveryIdSuffix}`}</span>}
            mono
          />
        )}
        <Field label="Last updated" value={fmtDate(ld.updated_at || ld.status_updated_at)} />
      </div>

      {/* Collapsible technical details */}
      {av && (
        <div data-testid="local-delivery-panel-technical">
          <button
            type="button"
            className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1"
            onClick={() => setTechOpen((v) => !v)}
            data-testid="local-delivery-panel-technical-toggle"
          >
            {techOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Technical details
          </button>
          {techOpen && (
            <div className="grid grid-cols-2 gap-3 mt-2 pt-2 border-t border-slate-100">
              <Field label="Validation ID" value={av.id || "—"} mono />
              <Field label="Provider" value={av.provider || "—"} />
              <Field label="Precision" value={av.precision || "—"} />
              <Field label="Serviceability" value={av.serviceability_status || "—"} />
              <Field label="Latitude" value={av.latitude != null ? av.latitude.toFixed(6) : "—"} mono />
              <Field label="Longitude" value={av.longitude != null ? av.longitude.toFixed(6) : "—"} mono />
              <Field label="Validated at" value={fmtDate(av.validated_at)} />
              <Field label="Expires at" value={fmtDate(av.expires_at)} />
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog — one concise step, business information
          only (order total, delivery fee, address city/state), never
          internal implementation details (no "authorization",
          "permit", "CAS", or similar). */}
      <Dialog open={confirmOpen} onOpenChange={(v) => !accepting && setConfirmOpen(v)}>
        <DialogContent data-testid="local-delivery-accept-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Request an Uber Direct courier?</DialogTitle>
            <DialogDescription>
              This will request a courier for this order now. The customer's delivery fee of{" "}
              {fmtMoney(latestQuote.customer_fee_amount, currency)} was already charged. This
              cannot be undone from here — if you need to cancel afterward, contact Uber support
              directly.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={accepting}>Cancel</Button>
            <Button onClick={acceptAndDispatch} disabled={accepting} data-testid="local-delivery-accept-confirm-btn">
              {accepting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Yes, request courier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm text-slate-900 ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</div>
    </div>
  );
}
